// Fixed CartPage.js - Use correct commission balance logic
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Chip,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Skeleton,
} from "@mui/material";
import {
  ArrowBack,
  Payment as PaymentIcon,
  ShoppingCart,
  CheckCircle,
  Add,
  Remove,
  Delete,
  LocationOn,
  ShoppingBag,
  LocalOffer,
  CardGiftcard,
  MonetizationOn,
  LocalShipping,
} from "@mui/icons-material";

import { calculateAvailableDiscounts } from "./SignupPage";
import { calculateCartProfitMargin } from "../utils/cartProfitMargin";
import CartPurchaseHandler from "../services/CartPurchaseHandler";
import CashfreeService from "../services/CashfreeService";
import ManualUpiPaymentBlock from "./ManualUpiPaymentBlock";
import { USE_CASHFREE_PAYMENTS } from "../config/paymentConfig";
import { confirmByFirestoreDocId } from "../services/CartOrderConfirmationService";
import { AddressService } from "../services/AddressService";
import OrderSuccessModal from "./OrderSuccessModal";
import { 
  doc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit
} from "firebase/firestore";
import { db } from "../firebase/config";
import { fetchReferralTreeOptimized } from "../services/ReferralTreeService";
import { calculateDeliveryFee, getDeliveryMessage } from "../utils/validation";
import {
  qualifiesForCashOutProgram,
  getCashOutBlockCount,
  getCashOutEnrollmentFee,
  cashOutRequiredWithDiscounts,
  CASH_OUT_FEE_PER_BLOCK,
  CASH_OUT_BLOCK_AMOUNT,
} from "../config/cashOutProgramConfig";
import {
  CASH_OUT_AFFILIATE_LEVEL_1_PERCENT,
  calculateCesEligibilityFromCounts as calculateCesEligibilityFromCountsConfig,
  calculateCesEligibilityFromReferralTree,
  countDirectUltraReferrals,
  countUltraMembersInTree,
  extractLegUltraCountsFromReferralTree,
} from "../config/cashOutAffiliateEligibilityConfig";

// Import the same components used in HomePage
import NavBar from "./NavBar";
import CartDrawer from "./CartDrawer";
import UserMenu from "./UserMenu";
import Footer from "./Footer";
import FloatingCartButton from "./FloatingCartButton";
import Notifications from "./Notifications";
import { brandPalette as primaryBlue } from "../config/brandTheme";

const CartPage = ({
  cart,
  userProfile,
  isAdmin,
  user,
  onBack,
  onUpdateQuantity,
  onRemoveItem,
  onCheckoutComplete,
}) => {
  const [paymentConfirmDialog, setPaymentConfirmDialog] = useState(false);
  const [paymentTransactionId, setPaymentTransactionId] = useState("");
  const [paymentTransactionIdError, setPaymentTransactionIdError] = useState("");
  const [loading, setLoading] = useState(false);
  const [orderSuccessModal, setOrderSuccessModal] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [shippingAddress, setShippingAddress] = useState({
    fullName: userProfile?.name || "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    phone: userProfile?.phoneNumber || "",
  });
  const [formErrors, setFormErrors] = useState({});

  const [discountInfo, setDiscountInfo] = useState({
    cashback: { balance: 0, availableDiscount: 0, percentage: 10 },
    commission: { balance: 0, availableDiscount: 0, adminFee: 0, netAmount: 0 },
  });
  const [appliedDiscounts, setAppliedDiscounts] = useState({
    useCashback: false,
    useCommission: false,
    cashbackAmount: 0,
    commissionAmount: 0,
    commissionAdminFee: 0,
  });
  const [cashOutEnroll, setCashOutEnroll] = useState(false);

  // CES Token functionality
  const [cashRoyaltyToggle, setCashRoyaltyToggle] = useState(false);
  const [cesEligibilityModalOpen, setCesEligibilityModalOpen] = useState(false);
  const [cesEligibilityModalData, setCesEligibilityModalData] = useState(null);
  const [cashRoyaltyAmount, setCashRoyaltyAmount] = useState(0);
  const [cesEligibilityData, setCesEligibilityData] = useState(null);
  const [referralTree, setReferralTree] = useState([]);
  const [referralTreeLoading, setReferralTreeLoading] = useState(false);
  
  // Cached counts for performance optimization
  const [cachedReferralCounts, setCachedReferralCounts] = useState({
    ultraMembers: 0,
    directReferrals: 0,
    lastUpdated: null,
    isValid: false
  });

  // HomePage UI states for consistency
  const [anchorEl, setAnchorEl] = useState(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categories] = useState(["All"]);

  // Address management states
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressLoading, setAddressLoading] = useState(true);
  const [useManualAddress, setUseManualAddress] = useState(false);

  const registrationFee = 499;

  // State for commission calculation (like WalletView)
  const [commissionStats, setCommissionStats] = useState(null);

  // FIXED: Add function to get actual commission balance (same as WalletView)
  const getActualCommissionBalance = (userProfile) => {
    // Priority: Use the calculated total from commissions if available (most accurate)
    // Fall back to userProfile balance fields (same logic as WalletView)
    if (commissionStats?.confirmedEarnings > 0) {
      return commissionStats.confirmedEarnings;
    }
    const newSystemBalance = userProfile?.commissionBalance || 0;
    const oldSystemBalance = userProfile?.confirmedCommissions || 0;
    return newSystemBalance > 0 ? newSystemBalance : oldSystemBalance;
  };

  // Fetch commission stats (simplified version of WalletView logic)
  const fetchCommissionStats = async () => {
    if (!user?.uid) return;

    try {
      const commissionsQuery = query(
        collection(db, "commissions"),
        where("referrerId", "==", user.uid),
        where("isMultiLevelCommission", "==", true),
        orderBy("createdAt", "desc")
      );
      const commissionsSnapshot = await getDocs(commissionsQuery);
      const commissions = commissionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const confirmedEarnings = commissions
        .filter((c) => c.status === "confirmed")
        .reduce(
          (sum, commission) => sum + (commission.commissionAmount || 0),
          0
        );

      setCommissionStats({
        confirmedEarnings,
        totalCommissions: commissions.length,
      });
    } catch (error) {
      console.error("Error fetching commission stats:", error);
      setCommissionStats({ confirmedEarnings: 0, totalCommissions: 0 });
    }
  };

  const getActualCommissionEarned = (userProfile) => {
    // Try new system first, then fall back to old system for migration
    const newSystemEarned = userProfile?.commissionEarned || 0;
    const oldSystemEarned = userProfile?.totalCommissions || 0;

    // If new system has data, use it. Otherwise, use old system data
    return newSystemEarned > 0 ? newSystemEarned : oldSystemEarned;
  };

  // Count Ultra Members from referral tree data (ProfileSettings.js structure)
  const countUltraMembers = (referralTree = []) => {
    let count = 0;
    console.log("🔢 Counting Ultra Members from tree:", referralTree);
    
    const countRecursive = (refs, level = 0) => {
      refs.forEach((referral, index) => {
        console.log(`🔍 Level ${level}, Index ${index}:`, {
          hasUserData: !!referral.referredUserData,
          hasPurchasedPackage: referral.referredUserData?.hasPurchasedPackage,
          subReferralsCount: referral.subReferrals?.length || 0
        });
        
        // Count if this referral is an Ultra Member
        if (referral.referredUserData?.hasPurchasedPackage === true) {
          count++;
          console.log(`✅ Ultra Member found at Level ${level}, Index ${index}. Total count: ${count}`);
        }
        // Recursively count in sub-referrals
        if (referral.subReferrals && referral.subReferrals.length > 0) {
          countRecursive(referral.subReferrals, level + 1);
        }
      });
    };
    
    countRecursive(referralTree);
    console.log(`🎯 Total Ultra Members: ${count}`);
    return count;
  };

  // Count Direct Referrals (Level 1 only) from referral tree - ONLY Ultra Members
  const countDirectReferrals = (referralTree = []) => {
    // Only count direct referrals (Level 1) who are Ultra Members
    console.log(`🔍 Counting Direct Referrals from tree of ${referralTree.length} items`);
    const ultraDirectReferrals = referralTree.filter((referral, index) => {
      const isUltra = referral.referredUserData?.hasPurchasedPackage === true;
      console.log(`  Referral ${index + 1}: ${referral.referredUserData?.email || referral.referredUserId} - Ultra: ${isUltra} (hasPurchasedPackage: ${referral.referredUserData?.hasPurchasedPackage})`);
      return isUltra;
    });
    const count = ultraDirectReferrals.length;
    console.log(`📊 Direct Referrals Count: ${count} Ultra Members out of ${referralTree.length} total direct referrals`);
    console.log(`📋 Ultra Direct Referrals:`, ultraDirectReferrals.map(r => ({
      email: r.referredUserData?.email,
      hasPurchasedPackage: r.referredUserData?.hasPurchasedPackage
    })));
    return count;
  };

  // Optimized CES Eligibility calculation using cached data
  const calculateCesEligibilityFromCache = () => {
    if (cachedReferralCounts.isValid) {
      return calculateCesEligibilityFromCounts(
        cachedReferralCounts.ultraMembers,
        cachedReferralCounts.directReferrals,
        cachedReferralCounts.legUltraCounts
      );
    }
    return null; // No valid cache available
  };

  const calculateCesEligibilityFromCounts = (
    ultraMembersCount,
    directReferralsCount,
    legUltraCounts = null
  ) =>
    calculateCesEligibilityFromCountsConfig(
      ultraMembersCount,
      directReferralsCount,
      legUltraCounts
    );

  const calculateCesEligibility = (referralTree = []) =>
    calculateCesEligibilityFromReferralTree(referralTree);

  // OPTIMIZED: Referral Tree Fetching using batch service
  const countTotalReferrals = (referrals) => {
    if (!referrals || referrals.length === 0) return 0;
    
    let count = referrals.length;
    referrals.forEach((referral) => {
      if (referral.subReferrals) {
        count += countTotalReferrals(referral.subReferrals);
      }
    });
    return count;
  };

  const fetchReferralTree = async () => {
    if (!user?.uid) return [];
    
    try {
      setReferralTreeLoading(true);
      // Use optimized service - fetches ALL data in just 2 queries
      const tree = await fetchReferralTreeOptimized(user.uid);
      return tree;
    } catch (error) {
      console.error("Error fetching referral tree:", error);
      return [];
    } finally {
      setReferralTreeLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile) {
      // FIXED: Create modified userProfile with actual commission balance
      const actualCommissionBalance = getActualCommissionBalance(userProfile);
      const modifiedUserProfile = {
        ...userProfile,
        commissionBalance: actualCommissionBalance,
      };

      const calculateCashbackDiscount = () => {
        const cartSubtotal = calculateCartTotals(cart, userProfile).subtotal;
        const availableCashback = userProfile?.cashbackBalance || 0;
        const cartProfitMargin = calculateCartProfitMargin(cart);
        const maxCashbackDiscount = Math.min(
          availableCashback,
          cartProfitMargin,
          Math.max(0, cartSubtotal)
        );

        return {
          balance: availableCashback,
          availableDiscount: maxCashbackDiscount,
          cartSubtotal,
          cartProfitMargin,
        };
      };

      const discounts = {
        cashback: calculateCashbackDiscount(),
        commission: calculateAvailableDiscounts(modifiedUserProfile).commission,
      };

      setDiscountInfo(discounts);
      setAppliedDiscounts((prev) =>
        prev.useCashback
          ? { ...prev, cashbackAmount: discounts.cashback.availableDiscount }
          : prev
      );
    }
  }, [userProfile, cart, commissionStats]);

  // Fetch commission stats when user changes (like WalletView)
  useEffect(() => {
    if (user?.uid) {
      fetchCommissionStats();
    }
  }, [user?.uid]);

  // Optimized referral tree loading with caching
  useEffect(() => {
    const loadReferralTreeWithCaching = async () => {
      if (user?.uid) {
        console.log("🔄 Loading referral data for CES (optimized)...");
        
        // Clear old cache versions on app load (one-time cleanup)
        const CACHE_VERSION = "v3"; // v3: 50% per-leg cap on team size (L2+)
        try {
          const allKeys = Object.keys(localStorage);
          allKeys.forEach(key => {
            if (key.startsWith('cesCache_')) {
              try {
                const cached = JSON.parse(localStorage.getItem(key));
                if (!cached.version || cached.version !== CACHE_VERSION) {
                  console.log(`🗑️ Clearing old cache: ${key}`);
                  localStorage.removeItem(key);
                }
              } catch (e) {
                // Invalid cache, remove it
                localStorage.removeItem(key);
              }
            }
          });
        } catch (e) {
          console.log("Cache cleanup skipped:", e);
        }
        
        // Try to get cached data first from localStorage
        const cacheKey = `cesCache_${user.uid}`;
        const cachedData = localStorage.getItem(cacheKey);
        
        if (cachedData) {
          try {
            const parsedCache = JSON.parse(cachedData);
            // Invalidate cache if version mismatch (fix for direct referrals counting)
            if (parsedCache.version !== CACHE_VERSION) {
              console.log("🔄 Cache version mismatch - invalidating old cache");
              localStorage.removeItem(cacheKey);
            } else {
              const cacheAge = Date.now() - new Date(parsedCache.lastUpdated).getTime();
              const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
              
              if (cacheAge < CACHE_DURATION) {
                console.log("⚡ Using cached CES data (fast load)");
                console.log(`📊 Cached - Ultra Members: ${parsedCache.ultraMembers}, Direct Referrals (Ultra): ${parsedCache.directReferrals}`);
                setCachedReferralCounts({
                  ultraMembers: parsedCache.ultraMembers,
                  directReferrals: parsedCache.directReferrals,
                  lastUpdated: parsedCache.lastUpdated,
                  isValid: true
                });
                // Still load tree in background for UI, but use cached counts for eligibility
                fetchReferralTree().then(tree => {
                  setReferralTree(tree);
                }).catch(err => console.log("Background tree fetch failed:", err));
                return; // Use cached data, skip Firebase call
              }
            }
          } catch (e) {
            console.log("Cache parsing failed, fetching fresh data");
          }
        }
        
        // Fetch fresh data and update cache
        const tree = await fetchReferralTree();
        console.log("📊 Fresh Referral Tree:", tree);
        
        const legUltraCounts = extractLegUltraCountsFromReferralTree(tree);
        const ultraCount = countUltraMembersInTree(tree);
        const directCount = countDirectUltraReferrals(tree);
        console.log("✨ Calculated - Ultra Members:", ultraCount, "Direct Referrals:", directCount, "Legs:", legUltraCounts);
        
        // Update cache with version
        const cacheData = {
          version: CACHE_VERSION, // Add version to cache
          ultraMembers: ultraCount,
          directReferrals: directCount,
          legUltraCounts,
          lastUpdated: new Date().toISOString()
        };
        
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        
        setCachedReferralCounts({
          ...cacheData,
          isValid: true
        });
        
        setReferralTree(tree);
        
      }
    };
    
    loadReferralTreeWithCaching();
  }, [user?.uid]);

  // Fetch user addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!user?.uid) return;
      
      try {
        setAddressLoading(true);
        const result = await AddressService.getUserAddresses(user.uid);
        
        if (result.success) {
          setAddresses(result.data);
          // Auto-select primary address if available and no address is selected
          const primaryAddress = result.data.find(addr => addr.isPrimary);
          if (primaryAddress && !selectedAddress) {
            setSelectedAddress(primaryAddress.id);
            // Auto-fill shipping address from selected address
            setShippingAddress({
              fullName: primaryAddress.fullName || userProfile?.name || "",
              addressLine1: primaryAddress.addressLine1 || "",
              addressLine2: primaryAddress.addressLine2 || "",
              city: primaryAddress.city || "",
              state: primaryAddress.state || "",
              pincode: primaryAddress.postalCode || "",
              phone: primaryAddress.phoneNumber || userProfile?.phoneNumber || "",
            });
          }
        } else {
          console.error("Failed to fetch addresses:", result.error);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error);
      } finally {
        setAddressLoading(false);
      }
    };

    fetchAddresses();
  }, [user?.uid, userProfile?.name, userProfile?.phoneNumber]);

  // HomePage styling - exact same styles

  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  const cardGlassStyles = {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.2)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 16px 48px rgba(11, 28, 72, 0.35)",
      background: "rgba(255, 255, 255, 0.25)",
    },
  };

  // FIXED: Centralized registration fee calculation function
  // UPDATED: Registration fee calculation with activation fee logic
  const calculateRegistrationFee = (cart, userProfile) => {
    // No registration fee for admins
    if (isAdmin) {
      return 0;
    }

    // No registration fee if user already purchased packages (eligible)
    if (userProfile?.hasPurchasedPackage) {
      return 0;
    }

    // No registration fee if cart doesn't contain any packages
    const hasPackages = cart.packages && cart.packages.length > 0;
    if (!hasPackages) {
      return 0;
    }

    // Check if cart contains "Activation Fee" package
    const hasActivationFee = cart.packages.some(
      (pkg) => pkg.category === "Activation Fee"
    );

    // If purchasing activation fee, no additional registration fee
    if (hasActivationFee) {
      return 0;
    }

    // HIDE the registration fee from users - it's included in package prices
    // Admin will see this internally but users won't see it as a line item
    return 0; // Changed from registrationFee to 0
  };

  // FIXED: Use centralized calculation function
  const calculateCartTotals = (
    cart,
    userProfile,
    appliedDiscounts = null,
    enrollCashOut = false
  ) => {
    const itemTotal = (cart.items || []).reduce(
      (sum, item) => sum + item.sellingPrice * item.quantity,
      0
    );
    
    // Calculate GST for items - GST is calculated on BASE PRICE, not selling price
    const itemGSTTotal = (cart.items || []).reduce(
      (sum, item) => {
        const gstPercentage = (item.gstPercentage !== undefined && item.gstPercentage !== null && item.gstPercentage !== '') ? Number(item.gstPercentage) : 18;
        const basePrice = item.basePrice || item.sellingPrice; // Use basePrice for GST
        const gstAmount = (basePrice * item.quantity * gstPercentage) / 100;
        return sum + gstAmount;
      },
      0
    );
    
    // Calculate money saved from compare prices
    // Savings = MRP - (selling price + GST) - NOT just MRP - selling price
    const originalItemTotal = (cart.items || []).reduce(
      (sum, item) => sum + (item.compareAtPrice || item.sellingPrice) * item.quantity,
      0
    );
    const priceSavings = originalItemTotal - (itemTotal + itemGSTTotal);

    const packageTotal = (cart.packages || []).reduce(
      (sum, pkg) => sum + pkg.price * pkg.quantity,
      0
    );

    const calculatedRegistrationFee = calculateRegistrationFee(
      cart,
      userProfile
    );
    const subtotal = itemTotal + itemGSTTotal + packageTotal;

    // Calculate discount amounts
    let cashbackDiscount = 0;
    let commissionDiscount = 0;
    let commissionAdminFee = 0;

    if (appliedDiscounts) {
      if (appliedDiscounts.useCashback) {
        cashbackDiscount = appliedDiscounts.cashbackAmount;
      }
      if (appliedDiscounts.useCommission) {
        commissionDiscount = appliedDiscounts.commissionAmount;
        commissionAdminFee = appliedDiscounts.commissionAdminFee;
      }
    }

    const totalDiscount = cashbackDiscount + commissionDiscount;
    const subtotalAfterDiscount = subtotal + calculatedRegistrationFee - totalDiscount;
    const deliveryFee = calculateDeliveryFee(subtotalAfterDiscount, user?.email);
    const cashOutApplies =
      enrollCashOut ||
      (appliedDiscounts &&
        cashOutRequiredWithDiscounts(appliedDiscounts) &&
        qualifiesForCashOutProgram(subtotal));
    const cashOutBlockCount = cashOutApplies
      ? getCashOutBlockCount(subtotal)
      : 0;
    const cashOutFee = cashOutApplies ? getCashOutEnrollmentFee(subtotal) : 0;
    const grandTotal =
      subtotalAfterDiscount + deliveryFee + cashOutFee;

    return {
      itemTotal,
      itemGSTTotal,
      packageTotal,
      registrationFee: calculatedRegistrationFee,
      subtotal,
      cashbackDiscount,
      commissionDiscount,
      commissionAdminFee,
      totalDiscount,
      priceSavings, // Money saved from compare prices
      originalItemTotal, // Total MRP before discount
      deliveryFee,
      cashOutFee,
      cashOutBlockCount,
      enrollCashOut: cashOutApplies,
      grandTotal: Math.max(0, grandTotal), // Ensure total is never negative
    };
  };

  const handleCashbackToggle = (event) => {
    const useCashback = event.target.checked;
    const cashbackAmount = useCashback
      ? discountInfo.cashback.availableDiscount
      : 0;

    setAppliedDiscounts((prev) => ({
      ...prev,
      useCashback,
      cashbackAmount,
    }));
    if (useCashback) {
      const sub = calculateCartTotals(cart, userProfile, {
        ...appliedDiscounts,
        useCashback,
        cashbackAmount,
      }).subtotal;
      if (qualifiesForCashOutProgram(sub)) {
        setCashOutEnroll(true);
      }
    }
  };

  const handleCashOutEnrollToggle = (event) => {
    if (cashOutMandatory) return;
    setCashOutEnroll(event.target.checked);
  };

  const handleCommissionToggle = (event) => {
    const useCommission = event.target.checked;
    const commissionAmount = useCommission
      ? discountInfo.commission.netAmount
      : 0;
    const commissionAdminFee = useCommission
      ? discountInfo.commission.adminFee
      : 0;

    // If Points Earnings is turned OFF, also turn off Cash toggle
    if (!useCommission && cashRoyaltyToggle) {
      setCashRoyaltyToggle(false);
      setCashRoyaltyAmount(0);
    }

    setAppliedDiscounts((prev) => ({
      ...prev,
      useCommission,
      commissionAmount,
      commissionAdminFee,
    }));
    if (useCommission) {
      const sub = calculateCartTotals(cart, userProfile, {
        ...appliedDiscounts,
        useCommission,
        commissionAmount,
        commissionAdminFee,
      }).subtotal;
      if (qualifiesForCashOutProgram(sub)) {
        setCashOutEnroll(true);
      }
    }
  };

  // Cash: % of Points Earnings credited to royaltyBalance on admin order confirm

  const handleCashRoyaltyToggle = async (event) => {
    const sendToRoyalty = event.target.checked;
    
    if (sendToRoyalty) {
      try {
        console.log("🎯 CES Toggle - Using optimized calculation");
        
        // OPTIMIZED: Try cached data first, then existing tree, then reasonable fallback
        let cesEligibility = calculateCesEligibilityFromCache();
        
        if (!cesEligibility && referralTree && referralTree.length > 0) {
          console.log("⚡ Using existing tree data...");
          cesEligibility = calculateCesEligibility(referralTree);
        }
        
        if (!cesEligibility) {
          console.log("🔄 No cached/tree data - fetching fresh referral tree...");
          // Fetch fresh data synchronously to get accurate eligibility
          setReferralTreeLoading(true);
          
          try {
            const freshTree = await fetchReferralTree();
            console.log("📊 Fresh Referral Tree fetched:", freshTree);
            
            if (freshTree && freshTree.length > 0) {
              cesEligibility = calculateCesEligibility(freshTree);
              
              // Update cache with fresh data
              const cacheKey = `cesCache_${user.uid}`;
              const CACHE_VERSION = "v3";
              const cacheData = {
                version: CACHE_VERSION,
                ultraMembers: cesEligibility.ultraMembersCount,
                directReferrals: cesEligibility.directReferralsCount,
                legUltraCounts: cesEligibility.legUltraCounts,
                lastUpdated: new Date().toISOString()
              };
              localStorage.setItem(cacheKey, JSON.stringify(cacheData));
              
              setCachedReferralCounts({
                ...cacheData,
                isValid: true
              });
              
              setReferralTree(freshTree);
              console.log("✅ Fresh eligibility calculated and cached");
            } else {
              console.log("⚠️ No referral tree found");
              cesEligibility = {
                currentLevel: 0,
                cesPercentage: 0,
                ultraMembersCount: 0,
                directReferralsCount: 0,
                nextLevel: { 
                  level: 1, 
                  cesPercentage: CASH_OUT_AFFILIATE_LEVEL_1_PERCENT,
                  ultraRequired: 10,
                  directReferrals: 10,
                  ultraShortfall: 10,
                  directReferralShortfall: 10
                },
                isEligible: false
              };
            }
          } catch (error) {
            console.error("Error fetching referral tree:", error);
            cesEligibility = {
              currentLevel: 0,
              cesPercentage: 0,
              ultraMembersCount: 0,
              directReferralsCount: 0,
              nextLevel: { 
                level: 1, 
                cesPercentage: CASH_OUT_AFFILIATE_LEVEL_1_PERCENT,
                ultraRequired: 10,
                directReferrals: 10,
                ultraShortfall: 10,
                directReferralShortfall: 10
              },
              isEligible: false
            };
          } finally {
            setReferralTreeLoading(false);
          }
        } else {
          console.log("⚡ Used cached/tree data - instant calculation!");
        }
        
        console.log("🎯 CES Eligibility Result:", cesEligibility);
        
        // Update CES eligibility data
        setCesEligibilityData(cesEligibility);
        
        // STRICT ENABLE LOGIC: Check all conditions for CES eligibility
        const canEnableCES = 
          cesEligibility.isEligible &&
          cesEligibility.cesPercentage > 0 &&
          cesEligibility.currentLevel > 0;
          
        console.log("🔑 CES Enable Check:", {
          isEligible: cesEligibility.isEligible,
          cesPercentage: cesEligibility.cesPercentage,
          currentLevel: cesEligibility.currentLevel,
          canEnableCES
        });
        
        // EXACT LOG FORMAT REQUESTED
        console.log("🧠 CES Toggle Eligibility Fix:",
          cesEligibility.isEligible,
          cesEligibility.currentLevel,
          cesEligibility.cesPercentage,
          "=> canEnableCES:", canEnableCES);
        
        // Block users who cannot enable CES
        if (!canEnableCES) {
          console.log("🚫 CES Toggle Blocked - User not eligible for CES transfer");
          // Show styled modal instead of alert
          setCesEligibilityModalData(cesEligibility);
          setCesEligibilityModalOpen(true);
          setCashRoyaltyToggle(false);
          return;
        }

        // Calculate transfer amount using level-based percentage
        const pointsBalance = getActualCommissionBalance(userProfile);
        // Use the user's current level percentage, or the next level's percentage if they haven't achieved any level yet
        const transferPercentage = cesEligibility.currentLevel > 0 
          ? cesEligibility.cesPercentage 
          : (cesEligibility.nextLevel?.cesPercentage ||
              CASH_OUT_AFFILIATE_LEVEL_1_PERCENT);
        
        const transferAmount = pointsBalance * (transferPercentage / 100);
        console.log(
          `Cash transfer scheduled: ${transferPercentage}% of ₹${pointsBalance.toFixed(
            2
          )} = ₹${transferAmount.toFixed(2)} (when your order is confirmed)`
        );
        setCashRoyaltyAmount(transferAmount);
        
        setCashRoyaltyToggle(true);
        
        // Reduce cart discount to the Points balance left after the royalty share
        if (appliedDiscounts.useCommission) {
          const remainingCommissionBalance = pointsBalance - transferAmount;
          const newCommissionNetAmount = Math.max(0, remainingCommissionBalance * 0.9); // After 10% admin fee
          const newCommissionAdminFee = Math.max(0, remainingCommissionBalance * 0.1);
          
          setAppliedDiscounts(prev => ({
            ...prev,
            commissionAmount: newCommissionNetAmount,
            commissionAdminFee: newCommissionAdminFee,
          }));
          
          console.log(
            `Updated commission discount: ₹${newCommissionNetAmount.toFixed(
              2
            )} (after Cash share)`
          );
        }
      } catch (error) {
        console.error("Error setting up Cash transfer:", error);
        // Reset the toggle state on error
        setCashRoyaltyToggle(false);
        setCashRoyaltyAmount(0);
        alert("Failed to set up Cash Wallet transfer. Please try again.");
        return;
      }
    } else {
      // When toggled off, restore the original commission discount amount
      setCashRoyaltyAmount(0);
      setCashRoyaltyToggle(false);
      console.log("🔄 CES Toggle disabled - user turned off the toggle");
      
      // NOTE: Wallet only displays confirmed balances, no need to clear cache
      console.log("💡 CES transfer cancelled - wallet shows only confirmed balances");
      
      // IMPORTANT FIX: Restore original commission discount when CES is disabled
      if (appliedDiscounts.useCommission) {
        const fullCommissionBalance = getActualCommissionBalance(userProfile);
        const originalNetAmount = fullCommissionBalance * 0.9; // After 10% admin fee
        const originalAdminFee = fullCommissionBalance * 0.1;
        
        setAppliedDiscounts(prev => ({
          ...prev,
          commissionAmount: originalNetAmount,
          commissionAdminFee: originalAdminFee,
        }));
        
        console.log(`Restored original commission discount: ₹${originalNetAmount.toFixed(2)}`);
      }
    }
    
    // NOTE: setCashRoyaltyToggle is now handled conditionally within each branch
    // Only set to true when user passes eligibility checks
    // Only set to false when user turns off an already enabled toggle
  };

  // FIXED: Use the centralized calculation
  const totals = calculateCartTotals(
    cart,
    userProfile,
    appliedDiscounts,
    cashOutEnroll
  );
  const {
    itemTotal,
    itemGSTTotal,
    packageTotal,
    registrationFee: totalRegistrationFee,
    subtotal,
    cashbackDiscount,
    commissionDiscount,
    commissionAdminFee,
    totalDiscount,
    deliveryFee,
    cashOutFee,
    cashOutBlockCount,
    grandTotal,
  } = totals;

  const cashOutEligible = qualifiesForCashOutProgram(subtotal);
  const cashOutMandatory = cashOutRequiredWithDiscounts(appliedDiscounts);
  const offeredCashOutBlocks = cashOutEligible
    ? getCashOutBlockCount(subtotal)
    : 0;
  const offeredCashOutFee = cashOutEligible
    ? getCashOutEnrollmentFee(subtotal)
    : 0;
  useEffect(() => {
    if (cashOutMandatory && cashOutEligible) {
      setCashOutEnroll(true);
    }
    if (!cashOutEligible) {
      setCashOutEnroll(false);
    }
  }, [cashOutMandatory, cashOutEligible]);

  // FIXED: Simplified needsRegistrationFee check
  const needsRegistrationFee = totalRegistrationFee > 0;
  
  // Calculate delivery message based on subtotal before delivery fee
  const subtotalForDelivery = subtotal + totalRegistrationFee - totalDiscount;
  const deliveryMessage = getDeliveryMessage(subtotalForDelivery);

  // HomePage functions for UI consistency
  const handleMenu = (event) => {
    // Since we're already on CartPage, clicking profile icon should go back to home
    if (onBack) {
      onBack();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleClose();
  };

  const handleCartDrawerClose = () => {
    setCartDrawerOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: "" });
  };

  const getCartItemCount = () => {
    return (
      cart.packages.reduce((sum, p) => sum + p.quantity, 0) +
      cart.items.reduce((sum, i) => sum + i.quantity, 0)
    );
  };

  // GST calculation function for sub-items - GST is calculated on BASE PRICE ONLY
  const calculateSubItemGST = (subItem) => {
    const packagePrice = subItem.itemPrice || 0; // This is the selling/package price (base + profit)
    const basePrice = subItem.basePrice || 0; // Use basePrice for GST calculation
    const profitMargin = subItem.profitMargin || 0; // Profit portion
    const gstPercentage = (subItem.gstPercentage !== undefined && subItem.gstPercentage !== null && subItem.gstPercentage !== '') ? Number(subItem.gstPercentage) : 18;
    
    // GST should only be calculated on the base price, not on profit margin
    const gstAmount = basePrice > 0 ? (basePrice * gstPercentage) / 100 : 0;
    
    // Final price = base price + profit + GST (on base price only)
    const totalWithGST = basePrice + profitMargin + gstAmount;
    
    return {
      basePrice: basePrice,
      profitMargin: profitMargin,
      packagePrice: packagePrice,
      gstPercentage: gstPercentage,
      gstAmount: gstAmount,
      totalWithGST: totalWithGST
    };
  };

  const getCartTotal = () => {
    const packageTotal = cart.packages.reduce(
      (sum, pkg) => {
        // For packages with sub-items, calculate GST properly
        if (pkg.hasSubItems && pkg.selectedSubItems && Object.keys(pkg.selectedSubItems).length > 0) {
          let subItemsTotal = 0;
          Object.entries(pkg.selectedSubItems).forEach(([subItemId, isSelected]) => {
            if (isSelected) {
              const subItem = pkg.subItems?.find(item => item.id.toString() === subItemId);
              if (subItem) {
                const quantity = pkg.subItemQuantities?.[subItemId] || 1;
                const gstInfo = calculateSubItemGST(subItem);
                subItemsTotal += gstInfo.totalWithGST * quantity;
              }
            }
          });
          return sum + (subItemsTotal * pkg.quantity);
        } 
        // For packages with sub-items that use selectedSubItemsList (array format)
        else if (pkg.isSubItemPackage && pkg.selectedSubItemsList && pkg.selectedSubItemsList.length > 0) {
          let subItemsTotal = 0;
          pkg.selectedSubItemsList.forEach((subItem) => {
            const quantity = subItem.selectedQuantity || 1;
            const gstInfo = calculateSubItemGST(subItem);
            subItemsTotal += gstInfo.totalWithGST * quantity;
          });
          return sum + (subItemsTotal * pkg.quantity);
        }
        // Regular packages WITHOUT sub-items - NO GST, use price as-is
        else {
          return sum + pkg.price * pkg.quantity;
        }
      },
      0
    );
    const itemTotal = cart.items.reduce(
      (sum, item) => sum + item.sellingPrice * item.quantity,
      0
    );
    return packageTotal + itemTotal;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const handleQuantityChange = (type, id, change) => {
    const currentItem = cart[type].find((item) => item.id === id);
    if (!currentItem) return;

    const newQuantity = currentItem.quantity + change;
    const minQuantity = currentItem.minOrderQuantity || 1;

    if (newQuantity <= 0) {
      onRemoveItem(type, id);
    } else if (newQuantity < minQuantity) {
      return; // Don't allow reducing below minimum
    } else {
      if (type === "items") {
        const stockLimit = currentItem.stockQuantity || 0;
        const maxOrderLimit = currentItem.maxOrderQuantity || Infinity;
        const maxQuantity = Math.min(stockLimit, maxOrderLimit);
        if (newQuantity <= maxQuantity) {
          onUpdateQuantity(type, id, newQuantity);
        }
      } else {
        onUpdateQuantity(type, id, newQuantity);
      }
    }
  };

  const handleAddressChange = (field) => (event) => {
    setShippingAddress((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSavedAddressChange = (event) => {
    const addressId = event.target.value;
    setSelectedAddress(addressId);
    
    if (addressId === "manual") {
      setUseManualAddress(true);
      // Keep current manual address values
      return;
    }
    
    setUseManualAddress(false);
    
    // Find and populate the selected address
    const selectedAddr = addresses.find(addr => addr.id === addressId);
    if (selectedAddr) {
      setShippingAddress({
        fullName: selectedAddr.fullName || "",
        addressLine1: selectedAddr.addressLine1 || "",
        addressLine2: selectedAddr.addressLine2 || "",
        city: selectedAddr.city || "",
        state: selectedAddr.state || "",
        pincode: selectedAddr.postalCode || "",
        phone: selectedAddr.phoneNumber || "",
      });
      
      // Clear any form errors
      setFormErrors({});
    }
  };

  const validateForm = () => {
    const errors = {};

    if (cart.items.length > 0) {
      if (!shippingAddress.fullName.trim())
        errors.fullName = "Full name is required";
      if (!shippingAddress.addressLine1.trim())
        errors.addressLine1 = "Address is required";
      if (!shippingAddress.city.trim()) errors.city = "City is required";
      if (!shippingAddress.state.trim()) errors.state = "State is required";
      if (!shippingAddress.pincode.trim())
        errors.pincode = "Pincode is required";
      if (!shippingAddress.phone.trim())
        errors.phone = "Phone number is required";
      if (shippingAddress.pincode && !/^\d{6}$/.test(shippingAddress.pincode)) {
        errors.pincode = "Please enter a valid 6-digit pincode";
      }

      if (
        shippingAddress.phone &&
        !/^\+?[\d\s-()]{10,}$/.test(shippingAddress.phone)
      ) {
        errors.phone = "Please enter a valid phone number";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateDiscountLimits = () => {
    const errors = [];
    const cartSubtotal = calculateCartTotals(cart, userProfile).subtotal;

    // Validate cashback limit - FIXED logic
    if (appliedDiscounts.useCashback) {
      const availableCashback = userProfile?.cashbackBalance || 0;
      const cartProfitMargin = calculateCartProfitMargin(cart);
      const maxAllowed = Math.min(
        availableCashback,
        cartProfitMargin,
        Math.max(0, cartSubtotal)
      );

      if (appliedDiscounts.cashbackAmount > maxAllowed) {
        errors.push(
          `Cashback discount cannot exceed ₹${maxAllowed.toFixed(
            2
          )} (maximum allowed for this cart)`
        );
      }

      if (appliedDiscounts.cashbackAmount > availableCashback) {
        errors.push(
          `Insufficient cashback balance. Available: ₹${availableCashback.toFixed(
            2
          )}`
        );
      }
    }

    // Validate commission limit
    if (appliedDiscounts.useCommission) {
      const totalCommissionRequired =
        appliedDiscounts.commissionAmount + appliedDiscounts.commissionAdminFee;
      if (totalCommissionRequired > discountInfo.commission.balance) {
        errors.push(`Insufficient Points Wallet balance`);
      }
    }

    // Validate minimum order amount
    const totals = calculateCartTotals(
      cart,
      userProfile,
      appliedDiscounts,
      cashOutEnroll
    );
    if (totals.grandTotal <= 0) {
      errors.push("Order total cannot be zero or negative");
    }

    if (cashOutMandatory && cashOutEligible && totals.cashOutFee <= 0) {
      errors.push(
        `Cashback enrollment is required when using Rewards or Points Wallet (add ₹${getCashOutEnrollmentFee(
          totals.subtotal
        ).toFixed(2)})`
      );
    }

    return errors;
  };

  const handleCheckout = () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message:
          "Please fill in all required shipping information",
      });
      return;
    }

    // NEW: Validate discount limits
    const discountErrors = validateDiscountLimits();
    if (discountErrors.length > 0) {
      setSnackbar({
        open: true,
        message: `Discount validation failed: ${discountErrors.join(", ")}`,
      });
      return;
    }

    setPaymentConfirmDialog(true);
  };

  // FIXED: Use the already calculated totals instead of recalculating
  const buildCartPurchasePayload = (orderId, paymentMethod, paymentDetails) => ({
    orderId,
    buyerId: user.uid,
    buyerName: userProfile.name,
    buyerEmail: user.email,
    items: cart.items || [],
    packages: cart.packages || [],
    itemTotal: itemTotal,
    packageTotal: packageTotal,
    registrationFee: totalRegistrationFee,
    subtotal: subtotal,
    appliedDiscounts: {
      cashbackDiscount,
      commissionDiscount,
      commissionAdminFee,
      totalDiscount,
      usedCashback: appliedDiscounts.useCashback,
      usedCommission: appliedDiscounts.useCommission,
    },
    totalMRP: totals.originalItemTotal || 0,
    gstAmount: totals.itemGSTTotal || 0,
    priceSavings: totals.priceSavings || 0,
    cashOutProgram:
      cashOutFee > 0 && cashOutBlockCount > 0
        ? {
            enrolled: true,
            blockCount: cashOutBlockCount,
            enrollmentFee: cashOutFee,
            orderSubtotal: subtotal,
            requiredWithDiscounts: cashOutMandatory,
          }
        : { enrolled: false },
    cashRoyaltyTransfer: cashRoyaltyToggle
      ? {
          enabled: true,
          transferAmount: cashRoyaltyAmount,
          transferPercentage:
            cesEligibilityData?.currentLevel > 0
              ? cesEligibilityData.cesPercentage
              : cesEligibilityData?.nextLevel?.cesPercentage ||
                CASH_OUT_AFFILIATE_LEVEL_1_PERCENT,
          fromPointsBalance: getActualCommissionBalance(userProfile),
          status: "pending_order_confirm",
        }
      : { enabled: false },
    grandTotal: grandTotal,
    totalAmount: grandTotal,
    paymentMethod,
    paymentDetails,
    userDetails: {
      myReferralId: userProfile?.myReferralId,
      referralIdUsed: userProfile?.referralIdUsed,
    },
    shippingAddress: shippingAddress,
  });

  const handlePaymentConfirm = async () => {
    setLoading(true);

    try {
      if (!USE_CASHFREE_PAYMENTS) {
        const txn = paymentTransactionId.trim();
        if (txn.length < 6) {
          setPaymentTransactionIdError(
            "Enter the UPI transaction ID from your payment app."
          );
          setLoading(false);
          return;
        }
        setPaymentTransactionIdError("");

        const result = await CartPurchaseHandler.processCartPurchase(
          buildCartPurchasePayload(null, "upi", {
            method: "upi",
            referenceNumber: txn,
            transactionDetails: txn,
          })
        );

        setLoading(false);
        setPaymentConfirmDialog(false);

        if (result.success) {
          setOrderData({
            totalOrders: 1,
            successfulOrders: 1,
            failedOrders: 0,
            totalAmount: grandTotal,
            orderIds: [result.cartOrderId],
            orderCounts: result.orderCounts,
            pendingAdminApproval: true,
          });
          setOrderSuccessModal(true);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to process order. Please try again.",
          });
        }
        return;
      }

      console.log("Creating cart order first...");

      // Step 1: Create cart order first to get our app order ID  
      const orderResult = await CartPurchaseHandler.createCartOrderForPayment({
        buyerId: user.uid,
        buyerName: userProfile.name,
        buyerEmail: user.email,
        items: cart.items || [],
        packages: cart.packages || [],
        grandTotal: grandTotal,
        shippingAddress: shippingAddress,
        cashOutProgram: cashOutFee > 0 && cashOutBlockCount > 0
          ? {
              enrolled: true,
              blockCount: cashOutBlockCount,
              enrollmentFee: cashOutFee,
              orderSubtotal: subtotal,
            }
          : { enrolled: false },
      });

      if (!orderResult.success) {
        throw new Error(orderResult.error || "Failed to create order");
      }

      // Step 2: Use the app order ID for Cashfree payment
      const paymentResult = await CashfreeService.initiatePayment(
        { 
          amount: grandTotal, 
          description: "Cart Purchase",
          orderId: orderResult.orderId, // Pass our app order ID to Cashfree
        },
        userProfile
      );

      if (!paymentResult.success) {
        throw new Error(paymentResult.error || "Payment failed. Please try again.");
      }

      console.log("Processing cart as single order...");

      let result = await CartPurchaseHandler.processCartPurchase(
        buildCartPurchasePayload(orderResult.orderId, "cashfree", {
          method: "cashfree",
          cashfreeOrderId: paymentResult.orderId,
          cashfreePaymentId: paymentResult.paymentId,
        })
      );

      if (result.success && result.docId) {
        const confirmResult = await confirmByFirestoreDocId(result.docId, {
          confirmedBy: "cashfree",
        });
        if (!confirmResult.success) {
          throw new Error(
            confirmResult.error ||
              "Payment received but order confirmation failed. Contact support."
          );
        }
      }

      setTimeout(() => {
        setLoading(false);
        setPaymentConfirmDialog(false);

        if (result.success) {
          // Set order data for the success modal
          setOrderData({
            totalOrders: 1, // Always 1 order now
            successfulOrders: 1,
            failedOrders: 0,
            totalAmount: grandTotal,
            orderIds: [result.cartOrderId], // Single order ID
            orderCounts: result.orderCounts, // Package and item counts
          });

          // Show success modal
          setOrderSuccessModal(true);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to process order. Please try again.",
          });
        }
      }, 2000);
    } catch (error) {
      console.error("Error processing cart checkout:", error);
      setLoading(false);
      setPaymentConfirmDialog(false);
      setSnackbar({
        open: true,
        message: error.message || "Failed to process order. Please try again.",
      });
    }
  };

  // Handle success modal close - navigate to homepage
  const handleSuccessModalClose = () => {
    setOrderSuccessModal(false);
    // Clear cart and navigate back to homepage
    onCheckoutComplete({
      ...orderData,
      skipAlert: true, // Add flag to skip browser alert
      navigateToHome: true, // Add flag to navigate to homepage
    });
  };

  // Handle continue shopping from modal - go directly to homepage
  const handleContinueShopping = () => {
    setOrderSuccessModal(false);
    // Navigate to homepage by calling onBack (which should go to homepage)
    onBack();
  };

  // Handle clicking outside modal - just close without triggering checkout complete
  const handleSuccessModalClickOutside = () => {
    setOrderSuccessModal(false);
    // Don't call onCheckoutComplete to avoid browser alert
  };

  const isCartEmpty = cart.packages.length === 0 && cart.items.length === 0;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
          ${primaryBlue[50]} 0%, 
          ${primaryBlue[100]} 25%, 
          ${primaryBlue[200]} 50%, 
          ${primaryBlue[100]} 75%, 
          ${primaryBlue[50]} 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* NavBar Component */}
        <NavBar
          user={user}
          handleMenu={handleMenu}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          categories={categories}
          cartItemCount={getCartItemCount()}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
          setCartDrawerOpen={setCartDrawerOpen}
          showSearch={false} // Hide search in cart page
          userProfile={userProfile}
        />

        {/* FloatingCartButton Component */}
        <FloatingCartButton
          getCartItemCount={getCartItemCount}
          setCartDrawerOpen={setCartDrawerOpen}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* CartDrawer Component */}
        <CartDrawer
          cartDrawerOpen={cartDrawerOpen}
          handleCartDrawerClose={handleCartDrawerClose}
          cart={cart}
          getCartItemCount={getCartItemCount}
          getCartTotal={getCartTotal}
          removeFromCart={onRemoveItem}
          updateCartQuantity={onUpdateQuantity}
          canAddMoreToCart={(item, currentCartQuantity) => {
            const stockLimit = item.stockQuantity || 0;
            const maxOrderLimit = item.maxOrderQuantity || Infinity;
            const maxQuantity = Math.min(stockLimit, maxOrderLimit);
            return currentCartQuantity < maxQuantity;
          }}
          formatPrice={formatPrice}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
          cardGlassStyles={cardGlassStyles}
          setShowCart={() => {}} // Already in cart
          setCartDrawerOpen={setCartDrawerOpen}
        />

        {/* Notifications Component */}
        <Notifications
          snackbar={snackbar}
          handleSnackbarClose={handleSnackbarClose}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* UserMenu Component */}
        <UserMenu
          anchorEl={anchorEl}
          handleClose={handleClose}
          isAdmin={isAdmin}
          onAdminPanel={() => {}} // Implement if needed
          handleSignOut={handleSignOut}
          setShowMyOrders={() => {}}
          setShowPackageStore={() => {}}
          setCartDrawerOpen={setCartDrawerOpen}
          setShowProfileSettings={() => {}}
          getCartItemCount={getCartItemCount}
          setDeleteDialogOpen={() => {}}
          deleteDialogOpen={false}
          handleDeleteAccount={() => {}}
          loading={false}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* Main Cart Content */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header with Back Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
              ...glassStyles,
              p: { xs: 2, sm: 3 },
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <IconButton
              onClick={onBack}
              sx={{
                mr: { xs: 1, sm: 2 },
                bgcolor: primaryBlue[500],
                color: "white",
                "&:hover": {
                  bgcolor: primaryBlue[600],
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant={{ xs: "h5", sm: "h4" }}
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Shopping Cart
            </Typography>
          </Box>

          {isCartEmpty ? (
            <Box
              sx={{
                ...glassStyles,
                p: 8,
                textAlign: "center",
                animation: "fadeIn 0.6s ease-in-out",
              }}
            >
              <ShoppingCart
                sx={{
                  fontSize: 120,
                  mb: 3,
                  color: primaryBlue[300],
                  opacity: 0.7,
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: primaryBlue[700],
                }}
              >
                Your cart is empty
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  color: primaryBlue[600],
                  opacity: 0.8,
                }}
              >
                Add some packages or items to get started!
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={onBack}
                sx={{
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                  boxShadow: `0 4px 20px ${primaryBlue[200]}`,
                  "&:hover": {
                    background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 25px ${primaryBlue[300]}`,
                  },
                }}
              >
                Continue Shopping
              </Button>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {/* Cart Items */}
              <Grid item xs={12} lg={8}>
                <Box sx={{ ...glassStyles, p: 4 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: primaryBlue[700],
                      mb: 3,
                    }}
                  >
                    Cart Items ({cart.packages.length + cart.items.length}{" "}
                    items)
                  </Typography>

                  {/* Packages Section */}
                  {cart.packages.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: primaryBlue[600],
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        <ShoppingBag sx={{ mr: 1 }} />
                        Packages ({cart.packages.length})
                      </Typography>
                      {cart.packages.map((pkg) => (
                        <Box
                          key={pkg.id}
                          sx={{
                            ...cardGlassStyles,
                            mb: 2,
                            p: { xs: 2, sm: 3 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", md: "row" },
                              gap: 2,
                            }}
                          >
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography
                                variant={{ xs: "h6", sm: "h6" }}
                                sx={{
                                  color: primaryBlue[700],
                                  fontWeight: 600,
                                  mb: 1,
                                }}
                              >
                                {pkg.name}
                              </Typography>
                              <Chip
                                label={pkg.category}
                                size="small"
                                sx={{
                                  mb: 1,
                                  bgcolor: primaryBlue[100],
                                  color: primaryBlue[700],
                                  fontWeight: 500,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ mb: 2, color: primaryBlue[600] }}
                              >
                                {pkg.description}
                              </Typography>
                              <Typography
                                variant={{ xs: "h6", sm: "h6" }}
                                sx={{
                                  color: primaryBlue[700],
                                  fontWeight: 700,
                                  mb: { xs: 2, md: 0 },
                                }}
                              >
                                {formatPrice(pkg.price)} each
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "row", md: "row" },
                                alignItems: "center",
                                justifyContent: {
                                  xs: "space-between",
                                  md: "flex-end",
                                },
                                gap: 2,
                                flexWrap: "wrap",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  bgcolor: "rgba(255, 255, 255, 0.3)",
                                  borderRadius: 2,
                                  p: 1,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleQuantityChange("packages", pkg.id, -1)
                                  }
                                  sx={{
                                    bgcolor: primaryBlue[500],
                                    color: "white",
                                    "&:hover": { bgcolor: primaryBlue[600] },
                                  }}
                                >
                                  <Add />
                                </IconButton>
                              </Box>
                              <Typography
                                variant={{ xs: "h6", sm: "h6" }}
                                sx={{
                                  fontWeight: 700,
                                  color: "#00BFFF",
                                  textAlign: { xs: "left", md: "right" },
                                }}
                              >
                                {formatPrice(pkg.price * pkg.quantity)}
                              </Typography>
                              <IconButton
                                onClick={() => onRemoveItem("packages", pkg.id)}
                                sx={{
                                  color: "#001A4D",
                                  "&:hover": {
                                    bgcolor: "rgba(244, 67, 54, 0.1)",
                                  },
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Items Section */}
                  {cart.items.length > 0 && (
                    <Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: primaryBlue[600],
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        <ShoppingCart sx={{ mr: 1 }} />
                        Items ({cart.items.length})
                      </Typography>
                      {cart.items.map((item) => (
                        <Box
                          key={item.id}
                          sx={{
                            ...cardGlassStyles,
                            mb: 2,
                            p: { xs: 2, sm: 3 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "row", sm: "row" },
                                alignItems: { xs: "flex-start", sm: "center" },
                                flexGrow: 1,
                                gap: 2,
                              }}
                            >
                              <Avatar
                                src={item.imageUrl}
                                sx={{
                                  width: { xs: 60, sm: 80 },
                                  height: { xs: 60, sm: 80 },
                                  border: `3px solid ${primaryBlue[200]}`,
                                  flexShrink: 0,
                                }}
                              >
                                <ShoppingCart />
                              </Avatar>
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                  variant={{ xs: "subtitle1", sm: "h6" }}
                                  sx={{
                                    color: primaryBlue[700],
                                    fontWeight: 600,
                                    mb: 1,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {item.name}
                                </Typography>
                                <Chip
                                  label={item.category}
                                  size="small"
                                  sx={{
                                    mb: 1,
                                    bgcolor: primaryBlue[100],
                                    color: primaryBlue[700],
                                    fontWeight: 500,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mb: 1,
                                    color: primaryBlue[600],
                                    display: { xs: "none", sm: "block" },
                                  }}
                                >
                                  {item.description}
                                </Typography>
                                <Typography
                                  variant={{ xs: "subtitle1", sm: "h6" }}
                                  sx={{
                                    color: primaryBlue[700],
                                    fontWeight: 700,
                                    mb: 1,
                                  }}
                                >
                                  {formatPrice(item.sellingPrice)} each
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#00BFFF", fontWeight: 500 }}
                                >
                                  Stock: {item.stockQuantity} available
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "row", sm: "row" },
                                alignItems: "center",
                                justifyContent: {
                                  xs: "space-between",
                                  sm: "flex-end",
                                },
                                gap: 2,
                                flexWrap: "wrap",
                                mt: { xs: 2, sm: 0 },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  bgcolor: "rgba(255, 255, 255, 0.3)",
                                  borderRadius: 2,
                                  p: 1,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleQuantityChange("items", item.id, -1)
                                  }
                                  sx={{
                                    bgcolor: primaryBlue[500],
                                    color: "white",
                                    "&:hover": { bgcolor: primaryBlue[600] },
                                  }}
                                >
                                  <Remove />
                                </IconButton>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    minWidth: 30,
                                    textAlign: "center",
                                    fontWeight: 600,
                                    color: primaryBlue[700],
                                  }}
                                >
                                  {item.quantity}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleQuantityChange("items", item.id, 1)
                                  }
                                  disabled={item.quantity >= Math.min(item.stockQuantity || 0, item.maxOrderQuantity || Infinity)}
                                  sx={{
                                    bgcolor:
                                      item.quantity >= Math.min(item.stockQuantity || 0, item.maxOrderQuantity || Infinity)
                                        ? "rgba(0,0,0,0.1)"
                                        : primaryBlue[500],
                                    color: "white",
                                    "&:hover": {
                                      bgcolor:
                                        item.quantity >= item.stockQuantity
                                          ? "rgba(0,0,0,0.1)"
                                          : primaryBlue[600],
                                    },
                                  }}
                                >
                                  <Add />
                                </IconButton>
                              </Box>
                              <Typography
                                variant={{ xs: "h6", sm: "h6" }}
                                sx={{
                                  fontWeight: 700,
                                  color: "#00BFFF",
                                  textAlign: { xs: "left", sm: "right" },
                                }}
                              >
                                {formatPrice(item.sellingPrice * item.quantity)}
                              </Typography>
                              <IconButton
                                onClick={() => onRemoveItem("items", item.id)}
                                sx={{
                                  color: "#001A4D",
                                  "&:hover": {
                                    bgcolor: "rgba(244, 67, 54, 0.1)",
                                  },
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Shipping Address */}
                {cart.items.length > 0 && (
                  <Box sx={{ ...glassStyles, p: 4, mt: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <LocationOn sx={{ mr: 1, color: primaryBlue[500] }} />
                      <Typography
                        variant="h6"
                        sx={{
                          color: primaryBlue[700],
                          fontWeight: 600,
                        }}
                      >
                        Shipping Address
                      </Typography>
                    </Box>

                    {/* Address Selection */}
                    {addressLoading ? (
                      <Box sx={{ mb: 3 }}>
                        <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" height={60} />
                      </Box>
                    ) : addresses.length === 0 ? (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mb: 3,
                          bgcolor: "rgba(0, 114, 206, 0.1)",
                          color: primaryBlue[700]
                        }}
                      >
                        No saved addresses found. You can add addresses in your Profile Settings to save time during checkout.
                      </Alert>
                    ) : (
                      <Box sx={{ mb: 3 }}>
                        <FormControl component="fieldset" sx={{ width: "100%" }}>
                          <FormLabel component="legend" sx={{ mb: 2, color: primaryBlue[700], fontWeight: 600 }}>
                            Choose Address
                          </FormLabel>
                          <RadioGroup
                            value={selectedAddress || "manual"}
                            onChange={handleSavedAddressChange}
                          >
                            {addresses.map((address) => (
                              <FormControlLabel
                                key={address.id}
                                value={address.id}
                                control={<Radio />}
                                sx={{ alignItems: "flex-start", mb: 1 }}
                                label={
                                  <Paper
                                    sx={{
                                      p: 2,
                                      width: "100%",
                                      ml: 1,
                                      border: selectedAddress === address.id 
                                        ? `2px solid ${primaryBlue[500]}`
                                        : "1px solid #e0e0e0",
                                      transition: "all 0.2s ease",
                                      bgcolor: selectedAddress === address.id 
                                        ? "rgba(0, 114, 206, 0.05)"
                                        : "rgba(255, 255, 255, 0.8)",
                                    }}
                                  >
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                      <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                          {address.fullName}
                                          {address.isPrimary && (
                                            <Chip
                                              label="Primary"
                                              size="small"
                                              color="primary"
                                              sx={{ ml: 1 }}
                                            />
                                          )}
                                          {address.label && (
                                            <Chip
                                              label={address.label}
                                              size="small"
                                              variant="outlined"
                                              sx={{ ml: 1 }}
                                            />
                                          )}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {address.addressLine1}
                                          {address.addressLine2 && `, ${address.addressLine2}`}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {address.city}, {address.state} {address.postalCode}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          Phone: {address.phoneNumber}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Paper>
                                }
                              />
                            ))}
                            
                            {/* Manual Address Option */}
                            <FormControlLabel
                              value="manual"
                              control={<Radio />}
                              sx={{ alignItems: "flex-start", mb: 1 }}
                              label={
                                <Paper
                                  sx={{
                                    p: 2,
                                    width: "100%",
                                    ml: 1,
                                    border: useManualAddress || selectedAddress === "manual"
                                      ? `2px solid ${primaryBlue[500]}`
                                      : "1px solid #e0e0e0",
                                    transition: "all 0.2s ease",
                                    bgcolor: useManualAddress || selectedAddress === "manual"
                                      ? "rgba(0, 114, 206, 0.05)"
                                      : "rgba(255, 255, 255, 0.8)",
                                  }}
                                >
                                  <Box display="flex" alignItems="center">
                                    <Add sx={{ mr: 1, color: primaryBlue[500] }} />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      Enter New Address
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Fill in the address fields below
                                  </Typography>
                                </Paper>
                              }
                            />
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    )}

                    {/* Manual Address Form */}
                    {(useManualAddress || selectedAddress === "manual" || addresses.length === 0) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ mb: 2, color: primaryBlue[700], fontWeight: 600 }}
                        >
                          Address Details
                        </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Full Name *"
                          value={shippingAddress.fullName}
                          onChange={handleAddressChange("fullName")}
                          error={!!formErrors.fullName}
                          helperText={formErrors.fullName}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Phone Number *"
                          value={shippingAddress.phone}
                          onChange={handleAddressChange("phone")}
                          error={!!formErrors.phone}
                          helperText={formErrors.phone}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Address Line 1 *"
                          value={shippingAddress.addressLine1}
                          onChange={handleAddressChange("addressLine1")}
                          error={!!formErrors.addressLine1}
                          helperText={formErrors.addressLine1}
                          placeholder="House/Flat No, Building Name, Street"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Address Line 2"
                          value={shippingAddress.addressLine2}
                          onChange={handleAddressChange("addressLine2")}
                          placeholder="Landmark, Area (Optional)"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="City *"
                          value={shippingAddress.city}
                          onChange={handleAddressChange("city")}
                          error={!!formErrors.city}
                          helperText={formErrors.city}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="State *"
                          value={shippingAddress.state}
                          onChange={handleAddressChange("state")}
                          error={!!formErrors.state}
                          helperText={formErrors.state}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Pincode *"
                          value={shippingAddress.pincode}
                          onChange={handleAddressChange("pincode")}
                          error={!!formErrors.pincode}
                          helperText={formErrors.pincode}
                          placeholder="6-digit pincode"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                      </Box>
                    )}
                  </Box>
                )}
              </Grid>

              {/* Order Summary */}
              <Grid item xs={12} lg={4}>
                <Box
                  sx={{
                    ...glassStyles,
                    p: { xs: 2, sm: 3, md: 4 },
                    position: { xs: "static", lg: "sticky" },
                    top: { lg: 100 },
                    height: "fit-content",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: primaryBlue[700],
                      fontWeight: 600,
                      mb: 3,
                    }}
                  >
                    Order Summary
                  </Typography>

                  {/* Order Summary Table */}
                  <TableContainer
                    sx={{
                      ...cardGlassStyles,
                      mb: 3,
                      overflowX: "auto",
                    }}
                  >
                    <Table
                      size="small"
                      sx={{ minWidth: { xs: 280, sm: "auto" } }}
                    >
                      <TableBody>
                        {/* Total MRP Row - Show when there are savings */}
                        {totals.priceSavings > 0 && totals.originalItemTotal > 0 && (
                          <TableRow sx={{ bgcolor: "rgba(255, 152, 0, 0.08)" }}>
                            <TableCell
                              sx={{ color: "#e65100", fontWeight: 600 }}
                            >
                              Total MRP
                              <Typography
                                variant="caption"
                                sx={{ display: "block", color: "text.secondary" }}
                              >
                                Original price before discount
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ 
                                color: "text.secondary", 
                                fontWeight: 600,
                                textDecoration: "line-through"
                              }}
                            >
                              {formatPrice(totals.originalItemTotal)}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Packages Row - Show when packages exist */}
                        {cart.packages.length > 0 && (
                          <TableRow>
                            <TableCell
                              sx={{ color: primaryBlue[700], fontWeight: 500 }}
                            >
                              Packages (
                              {cart.packages.reduce(
                                (sum, p) => sum + p.quantity,
                                0
                              )}
                              )
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: primaryBlue[700], fontWeight: 600 }}
                            >
                              {formatPrice(packageTotal)}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Items Row - Show when items exist */}
                        {cart.items.length > 0 && (
                          <TableRow>
                            <TableCell
                              sx={{ color: primaryBlue[700], fontWeight: 500 }}
                            >
                              Items (
                              {cart.items.reduce(
                                (sum, i) => sum + i.quantity,
                                0
                              )}
                              )
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: primaryBlue[700], fontWeight: 600 }}
                            >
                              {formatPrice(itemTotal)}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* GST Row - Show when items exist and have GST */}
                        {cart.items.length > 0 && itemGSTTotal > 0 && (
                          <TableRow>
                            <TableCell
                              sx={{ color: primaryBlue[700], fontWeight: 500 }}
                            >
                              GST on Items
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: primaryBlue[700], fontWeight: 600 }}
                            >
                              {formatPrice(itemGSTTotal)}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Subtotal */}
                        <TableRow>
                          <TableCell
                            sx={{ color: primaryBlue[700], fontWeight: 600 }}
                          >
                            <strong>Subtotal</strong>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: primaryBlue[700], fontWeight: 700 }}
                          >
                            <strong>{formatPrice(subtotal)}</strong>
                          </TableCell>
                        </TableRow>

                        {/* First Package Purchase Alert - FIXED: Show for package purchases */}
                        {!userProfile?.hasPurchasedPackage &&
                          cart.packages.length > 0 && (
                            <TableRow>
                              <TableCell
                                colSpan={2}
                                sx={{ p: 0, border: "none" }}
                              >
                                <Alert
                                  severity="info"
                                  sx={{
                                    my: 2,
                                    ...cardGlassStyles,
                                    bgcolor: "rgba(0, 114, 206, 0.1)",
                                    "& .MuiAlert-icon": {
                                      color: primaryBlue[600],
                                    },
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: primaryBlue[700] }}
                                  >
                                    <strong>🎉 First Package Purchase:</strong>{" "}
                                    This purchase will make you eligible for
                                    referral points on future orders!
                                    {cart.packages.some(
                                      (pkg) => pkg.category === "Activation Fee"
                                    )
                                      ? " You're purchasing the activation fee to unlock Points Wallet eligibility."
                                      : " Package purchase includes activation benefits."}
                                  </Typography>
                                </Alert>
                              </TableCell>
                            </TableRow>
                          )}

                        {/* Discount Rows */}
                        {cashbackDiscount > 0 && (
                          <TableRow>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <CardGiftcard
                                  sx={{ color: "#00BFFF", fontSize: 20 }}
                                />
                                <span
                                  style={{ color: "#00BFFF", fontWeight: 500 }}
                                >
                                  Cashback Discount
                                </span>
                              </Box>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "#00BFFF", fontWeight: 600 }}
                            >
                              -{formatPrice(cashbackDiscount)}
                            </TableCell>
                          </TableRow>
                        )}

                        {commissionDiscount > 0 && (
                          <TableRow>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <MonetizationOn
                                  sx={{ color: "#1E40AF", fontSize: 20 }}
                                />
                                <span
                                  style={{ color: "#1E40AF", fontWeight: 500 }}
                                >
                                  Points Wallet discount
                                </span>
                              </Box>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "#1E40AF", fontWeight: 600 }}
                            >
                              -{formatPrice(commissionDiscount)}
                            </TableCell>
                          </TableRow>
                        )}

                        {commissionAdminFee > 0 && (
                          <TableRow>
                            <TableCell>
                              <span
                                style={{
                                  color: "#D4AF37",
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                }}
                              >
                                Points Wallet admin fee (10%)
                              </span>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "#D4AF37", fontWeight: 600 }}
                            >
                              +{formatPrice(commissionAdminFee)}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Price Savings Row */}
                        {totals.priceSavings > 0 && (
                          <TableRow>
                            <TableCell>
                              <Typography
                                variant="body1"
                                sx={{ color: "#00BFFF", fontWeight: 500 }}
                              >
                                Price Savings (MRP)
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body1"
                                sx={{ color: "#00BFFF", fontWeight: 600 }}
                              >
                                -₹{totals.priceSavings.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {cashOutFee > 0 && (
                          <TableRow>
                            <TableCell>
                              <Typography
                                variant="body1"
                                sx={{ color: "#7c3aed", fontWeight: 500 }}
                              >
                                Cashback ({cashOutBlockCount} × ₹
                                {CASH_OUT_FEE_PER_BLOCK})
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body1"
                                sx={{ color: "#7c3aed", fontWeight: 600 }}
                              >
                                +{formatPrice(cashOutFee)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Delivery Fee Row */}
                        <TableRow>
                          <TableCell>
                            <Typography
                              variant="body1"
                              sx={{ color: primaryBlue[700], fontWeight: 500 }}
                            >
                              Delivery Fee
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body1"
                              sx={{ color: primaryBlue[700], fontWeight: 600 }}
                            >
                              {deliveryFee === 0 ? 'FREE' : formatPrice(deliveryFee)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={2}>
                            <Divider sx={{ bgcolor: primaryBlue[200] }} />
                          </TableCell>
                        </TableRow>

                        {/* Total */}
                        <TableRow>
                          <TableCell>
                            <Typography
                              variant="h6"
                              sx={{ color: primaryBlue[800], fontWeight: 700 }}
                            >
                              Total
                              {(totalDiscount > 0 || totals.priceSavings > 0) && (
                                <Chip
                                  label={`₹${(totalDiscount + totals.priceSavings).toFixed(2)} saved!`}
                                  size="small"
                                  color="success"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="h6"
                              sx={{
                                color: "#00BFFF",
                                fontWeight: 700,
                                fontSize: "1.3rem",
                              }}
                            >
                              {formatPrice(grandTotal)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Delivery Information */}
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 3,
                      background: "rgba(0, 114, 206, 0.1)",
                      backdropFilter: "blur(15px)",
                      border: `2px solid ${primaryBlue[200]}`,
                      borderRadius: "20px",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <LocalShipping sx={{ color: primaryBlue[600], mr: 1 }} />
                        <Typography
                          variant="h6"
                          sx={{
                            color: primaryBlue[700],
                            fontWeight: 600,
                          }}
                        >
                          Delivery Information
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body1"
                          sx={{ 
                            color: primaryBlue[700], 
                            fontWeight: 600,
                            mb: 0.5
                          }}
                        >
                          📦 Delivery in 7–10 days
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[600] }}
                        >
                          Your order will be delivered within 7 to 10 business days from order confirmation
                        </Typography>
                      </Box>

                      {deliveryMessage.isFree ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CheckCircle sx={{ color: "#00BFFF", mr: 1, fontSize: 20 }} />
                          <Typography
                            variant="body1"
                            sx={{ color: "#00BFFF", fontWeight: 600 }}
                          >
                            {deliveryMessage.message}
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: primaryBlue[600], fontWeight: 500 }}
                          >
                            {deliveryMessage.message}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: primaryBlue[500] }}
                          >
                            {deliveryMessage.additionalMessage}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: primaryBlue[400], mt: 0.5 }}
                          >
                            Free shipping on orders above ₹2,999
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* FIXED: Discount Application Section - Show for BOTH packages and items */}
                  {(discountInfo.cashback.availableDiscount > 0 ||
                    discountInfo.commission.netAmount > 0 ||
                    cashOutEligible) && (
                    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          color: primaryBlue[700],
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        <LocalOffer sx={{ mr: 1, verticalAlign: "middle" }} />
                        Available Discounts
                      </Typography>

                      {/* Cashback Discount Card */}
                      {discountInfo.cashback.balance > 0 &&
                        (discountInfo.cashback.cartProfitMargin || 0) === 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Cashback balance ₹
                            {discountInfo.cashback.balance.toFixed(2)} — items in
                            this cart have no referral profit margin, so cashback
                            cannot be applied on this order.
                          </Alert>
                        )}

                      {discountInfo.cashback.availableDiscount > 0 && (
                        <Card
                          variant="outlined"
                          sx={{ mb: 2, ...cardGlassStyles }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexGrow: 1,
                                }}
                              >
                                <CardGiftcard
                                  sx={{ color: "#00BFFF", mr: 2, fontSize: 28 }}
                                />
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 600,
                                      color: primaryBlue[700],
                                    }}
                                  >
                                    Cashback Discount
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Balance ₹
                                    {discountInfo.cashback.balance.toFixed(2)} —
                                    apply up to ₹
                                    {discountInfo.cashback.availableDiscount.toFixed(
                                      2
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{ color: "#00BFFF", fontWeight: 700 }}
                                  >
                                    Save ₹
                                    {discountInfo.cashback.availableDiscount.toFixed(
                                      2
                                    )}
                                  </Typography>
                                </Box>
                              </Box>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={appliedDiscounts.useCashback}
                                    onChange={handleCashbackToggle}
                                    color="success"
                                  />
                                }
                                label=""
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      )}

                      {/* Points Discount Card */}
                      {discountInfo.commission.netAmount > 0 && (
                        <Card
                          variant="outlined"
                          sx={{ mb: 2, ...cardGlassStyles }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexGrow: 1,
                                }}
                              >
                                <MonetizationOn
                                  sx={{ color: "#1E40AF", mr: 2, fontSize: 28 }}
                                />
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 600,
                                      color: primaryBlue[700],
                                    }}
                                  >
                                    Points Wallet
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    ₹
                                    {(cashRoyaltyToggle && appliedDiscounts.useCommission 
                                      ? (getActualCommissionBalance(userProfile) - cashRoyaltyAmount)
                                      : getActualCommissionBalance(userProfile)
                                    ).toFixed(2)}{" "}
                                    Points Wallet balance
                                    {cashRoyaltyToggle && cashRoyaltyAmount > 0 && appliedDiscounts.useCommission && (
                                      <span style={{ color: '#D4AF37', fontWeight: 600 }}>
                                        {" "}(₹{cashRoyaltyAmount.toFixed(2)} → Cash Wallet)
                                      </span>
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5 }}
                                  >
                                    Usable amount: ₹{(appliedDiscounts.useCommission
                                      ? appliedDiscounts.commissionAmount
                                      : discountInfo.commission.netAmount
                                    ).toFixed(2)} (after 10% admin fee)
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{ color: "#1E40AF", fontWeight: 700 }}
                                  >
                                    Save ₹
                                    {(appliedDiscounts.useCommission
                                      ? appliedDiscounts.commissionAmount
                                      : discountInfo.commission.netAmount
                                    ).toFixed(2)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Admin fee: ₹
                                    {discountInfo.commission.adminFee.toFixed(
                                      2
                                    )}
                                  </Typography>
                                </Box>
                              </Box>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={appliedDiscounts.useCommission}
                                    onChange={handleCommissionToggle}
                                    color="primary"
                                  />
                                }
                                label=""
                              />
                            </Box>
                            
                            {/* Send to Cash Wallet — only when Points Earnings is on */}
                            {getActualCommissionBalance(userProfile) > 0 && appliedDiscounts.useCommission && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(0, 0, 0, 0.12)" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#D4AF37",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      ⭐ Send to Cash Wallet
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {(() => {
                                        // OPTIMIZED: Always try to show immediate results, never show "Loading..."
                                        let cesEligibility = calculateCesEligibilityFromCache();
                                        
                                        // If no cache, use fallback calculation with tree data (if available)
                                        if (!cesEligibility && referralTree && referralTree.length > 0) {
                                          cesEligibility = calculateCesEligibility(referralTree);
                                        }
                                        
                                        // If still no data, provide a reasonable fallback (assume Level 0)
                                        if (!cesEligibility) {
                                          cesEligibility = {
                                            currentLevel: 0,
                                            cesPercentage: 0,
                                            ultraMembersCount: 0,
                                            directReferralsCount: 0,
                                            nextLevel: { 
                                              level: 1, 
                                              cesPercentage:
                                                CASH_OUT_AFFILIATE_LEVEL_1_PERCENT,
                                              ultraRequired: 10,
                                              directReferrals: 10,
                                              ultraShortfall: 10,
                                              directReferralShortfall: 10
                                            },
                                            isEligible: false // Proper eligibility check - must reach Level 1
                                          };
                                        }
                                        
                                        const eligibilityResult = cesEligibility;
                                        console.log("🎨 UI Display - CES Eligibility (optimized):", eligibilityResult);
                                        
                                        // Use the same logic as the toggle handler for consistency
                                        const transferPercentage = eligibilityResult.currentLevel > 0 
                                          ? eligibilityResult.cesPercentage 
                                          : (eligibilityResult.nextLevel?.cesPercentage ||
                                              CASH_OUT_AFFILIATE_LEVEL_1_PERCENT);
                                        const transferAmount = getActualCommissionBalance(userProfile) * (transferPercentage / 100);
                                        
                                        if (eligibilityResult.isEligible) {
                                          return `Transfer ${transferPercentage}% (₹${transferAmount.toFixed(2)}) to Cash Wallet — Level ${eligibilityResult.currentLevel}`;
                                        } else {
                                          // Build message showing only values greater than 0
                                          const ultraShortfall = Math.max(0, eligibilityResult.nextLevel?.ultraShortfall || 0);
                                          const directShortfall = Math.max(0, eligibilityResult.nextLevel?.directReferralShortfall || 0);
                                          
                                          const requirements = [];
                                          if (ultraShortfall > 0) {
                                            requirements.push(`${ultraShortfall} more Ultra Members`);
                                          }
                                          if (directShortfall > 0) {
                                            requirements.push(`${directShortfall} more Direct Referrals`);
                                          }
                                          
                                          if (requirements.length === 0) {
                                            return "Not eligible yet. Check other requirements for next level.";
                                          }
                                          
                                          const requirementText = requirements.join(' & ');
                                          return `Not eligible yet. Need ${requirementText} for Level ${eligibilityResult.nextLevel?.level || 1}`;
                                        }
                                      })()}
                                    </Typography>
                                    {cashRoyaltyToggle && (
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "#D4AF37", fontWeight: 600, mt: 0.5 }}
                                      >
                                        Cash Wallet: +₹{cashRoyaltyAmount.toFixed(2)}
                                      </Typography>
                                    )}
                                  </Box>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={cashRoyaltyToggle}
                                        onChange={handleCashRoyaltyToggle}
                                        disabled={(() => {
                                          let cesEligibility = calculateCesEligibilityFromCache();
                                          if (!cesEligibility && referralTree && referralTree.length > 0) {
                                            cesEligibility = calculateCesEligibility(referralTree);
                                          }
                                          if (!cesEligibility) {
                                            cesEligibility = { isEligible: false, currentLevel: 0, cesPercentage: 0 };
                                          }
                                          
                                          // Use the same logic as toggle handler
                                          const canEnableCES = 
                                            cesEligibility.isEligible &&
                                            cesEligibility.cesPercentage > 0 &&
                                            cesEligibility.currentLevel > 0;
                                          
                                          console.log("🔒 CES Switch Disabled Check (Consistent):", {
                                            isEligible: cesEligibility.isEligible,
                                            cesPercentage: cesEligibility.cesPercentage,
                                            currentLevel: cesEligibility.currentLevel,
                                            canEnableCES,
                                            isDisabled: !canEnableCES
                                          });
                                          return !canEnableCES;
                                        })()}
                                        sx={{
                                          '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#D4AF37',
                                          },
                                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#D4AF37',
                                          },
                                        }}
                                      />
                                    }
                                    label=""
                                  />
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Cash Out Program — separate from wallet discounts */}
                      {cashOutEligible && (
                        <Card
                          variant="outlined"
                          sx={{
                            mb: 2,
                            ...cardGlassStyles,
                            borderColor: "#7c3aed",
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 2,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 700,
                                    color: primaryBlue[800],
                                  }}
                                >
                                  Enroll to Cashback
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  Order subtotal ₹
                                  {subtotal.toLocaleString("en-IN")} —{" "}
                                  {offeredCashOutBlocks} program
                                  {offeredCashOutBlocks === 1 ? "" : "s"} (₹
                                  {CASH_OUT_BLOCK_AMOUNT.toLocaleString("en-IN")}{" "}
                                  each) × ₹
                                  {CASH_OUT_FEE_PER_BLOCK} ={" "}
                                  <strong>
                                    ₹{offeredCashOutFee.toLocaleString("en-IN")}
                                  </strong>{" "}
                                  added to your total when enrolled.
                                </Typography>
                                {cashOutMandatory && (
                                  <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
                                    Required because you selected Cashback or Points
                                    Wallet.
                                  </Alert>
                                )}
                              </Box>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={cashOutMandatory || cashOutEnroll}
                                    onChange={handleCashOutEnrollToggle}
                                    disabled={cashOutMandatory}
                                    color="secondary"
                                  />
                                }
                                label=""
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      )}

                      {/* Applied Discounts Summary */}
                      {(appliedDiscounts.useCashback ||
                        appliedDiscounts.useCommission ||
                        cashRoyaltyToggle ||
                        (cashOutEnroll && cashOutFee > 0)) && (
                        <Alert severity="success" sx={{ ...cardGlassStyles }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            🎉 Total Savings: ₹{totalDiscount.toFixed(2)}
                          </Typography>
                          <Typography variant="body2">
                            {appliedDiscounts.useCashback &&
                              `Cashback: ₹${cashbackDiscount.toFixed(2)}`}
                            {appliedDiscounts.useCashback &&
                              appliedDiscounts.useCommission &&
                              " + "}
                            {appliedDiscounts.useCommission &&
                              `Points Wallet: ₹${commissionDiscount.toFixed(2)}`}
                          </Typography>
                          {cashRoyaltyToggle && cashRoyaltyAmount > 0 && appliedDiscounts.useCommission && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mt: 1, 
                                color: "#D4AF37", 
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center"
                              }}
                            >
                              ⭐ Cash Wallet: +₹{cashRoyaltyAmount.toFixed(2)}
                            </Typography>
                          )}
                          {cashOutEnroll && cashOutFee > 0 && (
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                              Cashback: +₹{cashOutFee.toFixed(2)} (
                              {cashOutBlockCount} ID
                              {cashOutBlockCount === 1 ? "" : "s"})
                            </Typography>
                          )}
                        </Alert>
                      )}
                    </Box>
                  )}

                  {/* Payment Section - Clean & Focused */}
                  <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Typography
                      variant={{ xs: "subtitle1", sm: "h6" }}
                      gutterBottom
                      sx={{
                        color: primaryBlue[700],
                        fontWeight: 600,
                        mb: { xs: 1, sm: 2 },
                        textAlign: "center",
                      }}
                    >
                      Complete Your Order
                    </Typography>
                    
                    {/* Total Amount Card - Prominent */}
                    <Box
                      sx={{
                        ...cardGlassStyles,
                        bgcolor: "linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05))",
                        p: { xs: 3, sm: 4 },
                        mb: 3,
                        textAlign: "center",
                        border: "2px solid #00BFFF",
                        borderRadius: "16px",
                        boxShadow: "0 8px 32px rgba(76, 175, 80, 0.15)",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ 
                          color: "#2e7d32", 
                          fontWeight: 600, 
                          mb: 1,
                          fontSize: { xs: "1rem", sm: "1.1rem" }
                        }}
                      >
                        Total Amount
                      </Typography>
                      <Typography
                        variant="h2"
                        sx={{
                          color: "#2e7d32",
                          fontWeight: 800,
                          fontSize: { xs: "2.5rem", sm: "3rem" },
                          textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        {formatPrice(grandTotal)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ 
                          color: "#388e3c", 
                          mt: 1,
                          opacity: 0.8
                        }}
                      >
                        Including all taxes and delivery charges
                      </Typography>
                    </Box>

                    {/* Primary Payment Button */}
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={handleCheckout}
                      startIcon={<PaymentIcon />}
                      disabled={loading}
                      sx={{
                        py: { xs: 2.5, sm: 3 },
                        background: `linear-gradient(135deg, #00BFFF, #60A5FA, #81c784)`,
                        fontWeight: 700,
                        fontSize: { xs: "1.1rem", sm: "1.3rem" },
                        borderRadius: "12px",
                        boxShadow: "0 6px 24px rgba(76, 175, 80, 0.4)",
                        textTransform: "none",
                        "&:hover": {
                          background: `linear-gradient(135deg, #388e3c, #00BFFF, #60A5FA)`,
                          transform: "translateY(-3px)",
                          boxShadow: "0 8px 32px rgba(76, 175, 80, 0.5)",
                        },
                        "&:disabled": {
                          background: "rgba(0, 0, 0, 0.12)",
                          color: "rgba(0, 0, 0, 0.5)",
                          transform: "none",
                          boxShadow: "none",
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      {loading ? (
                        <>
                          <Box sx={{ mr: 1 }}>⏳</Box>
                          Processing Payment...
                        </>
                      ) : (
                        <>
                          🚀 Pay Now - Quick & Secure
                        </>
                      )}
                    </Button>

                    {/* Payment Security Info */}
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: "rgba(0, 114, 206, 0.05)",
                        borderRadius: "8px",
                        border: "1px solid rgba(0, 114, 206, 0.2)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          textAlign: "center",
                          color: primaryBlue[600],
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        🔐 100% Secure Payment
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: "center",
                          color: primaryBlue[500],
                          mt: 0.5,
                          display: "block",
                        }}
                      >
                        UPI • Cards • Net Banking • Wallets
                      </Typography>
                    </Box>

                    {/* First Package Purchase Bonus */}
                    {!userProfile?.hasPurchasedPackage &&
                      cart.packages.length > 0 && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: "rgba(255, 193, 7, 0.1)",
                            borderRadius: "8px",
                            border: "1px solid rgba(255, 193, 7, 0.3)",
                            textAlign: "center",
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: "#f57c00", 
                              fontWeight: 600,
                              fontSize: "0.9rem" 
                            }}
                          >
                            🎉 Bonus: Unlock referral rewards with your first package!
                          </Typography>
                        </Box>
                      )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </Container>

        {/* Footer Component */}
        <Footer primaryBlue={primaryBlue} glassStyles={glassStyles} />
      </Box>

      {/* Payment Confirmation Dialog */}
      <Dialog
        open={paymentConfirmDialog}
        onClose={() => !loading && setPaymentConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...glassStyles,
            bgcolor: "rgba(255, 255, 255, 0.95)",
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CheckCircle sx={{ color: "#00BFFF", mr: 1 }} />
            <Typography
              variant="h6"
              sx={{ color: primaryBlue[700], fontWeight: 600 }}
            >
              Confirm Order
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {USE_CASHFREE_PAYMENTS ? (
            <Typography
              variant="body1"
              gutterBottom
              sx={{ color: primaryBlue[700] }}
            >
              You will be redirected to Cashfree to pay{" "}
              <strong style={{ color: "#00BFFF" }}>
                {formatPrice(grandTotal)}
              </strong>{" "}
              securely.
            </Typography>
          ) : (
            <ManualUpiPaymentBlock
              amountLabel={formatPrice(grandTotal)}
              transactionId={paymentTransactionId}
              onTransactionIdChange={setPaymentTransactionId}
              transactionIdError={paymentTransactionIdError}
            />
          )}

          <Typography variant="body2" sx={{ mt: 2, color: primaryBlue[600] }}>
            This order includes:
            {cart.packages.length > 0 && (
              <>
                <br />• {cart.packages.length} package(s) -{" "}
                <strong>{formatPrice(packageTotal)}</strong>
              </>
            )}
            {cart.items.length > 0 && (
              <>
                <br />• {cart.items.length} item(s) -{" "}
                <strong>{formatPrice(itemTotal)}</strong>
              </>
            )}
            {needsRegistrationFee && (
              <>
                <br />• Registration Fee -{" "}
                <strong>{formatPrice(totalRegistrationFee)}</strong>
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setPaymentConfirmDialog(false)}
            disabled={loading}
            sx={{ color: primaryBlue[600] }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePaymentConfirm}
            variant="contained"
            disabled={loading}
            sx={{
              background: `linear-gradient(45deg, #00BFFF, #60A5FA)`,
              "&:hover": {
                background: `linear-gradient(45deg, #388e3c, #00BFFF)`,
              },
            }}
          >
            {loading
              ? "Processing..."
              : USE_CASHFREE_PAYMENTS
                ? "Proceed to Pay"
                : "I've paid — submit for approval"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CES Eligibility Modal */}
      <Dialog
        open={cesEligibilityModalOpen}
        onClose={() => setCesEligibilityModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: `linear-gradient(135deg, ${primaryBlue[50]} 0%, #fff 100%)`,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle sx={{ 
          color: primaryBlue[700], 
          fontWeight: 600, 
          borderBottom: `2px solid ${primaryBlue[100]}`,
          background: `linear-gradient(135deg, ${primaryBlue[100]} 0%, ${primaryBlue[50]} 100%)`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              background: `linear-gradient(135deg, #D4AF37, #ffb74d)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}>
              ⭐
            </Box>
            Cash Wallet eligibility
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {cesEligibilityModalData && (
            <Box>
              <Typography variant="h6" sx={{ 
                color: primaryBlue[800], 
                mb: 2, 
                fontWeight: 500,
                textAlign: 'center'
              }}>
                You are not eligible to send Points Wallet balance to Cash Wallet yet
              </Typography>

              <Card sx={{ 
                mb: 3, 
                background: `linear-gradient(135deg, ${primaryBlue[50]} 0%, #f8f9fa 100%)`,
                border: `1px solid ${primaryBlue[200]}`,
              }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    color: primaryBlue[700],
                    mb: 1
                  }}>
                    Current Status:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h4" sx={{ 
                          color: '#00BFFF', 
                          fontWeight: 'bold' 
                        }}>
                          {cesEligibilityModalData.ultraMembersCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ultra Members
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h4" sx={{ 
                          color: '#1E40AF', 
                          fontWeight: 'bold' 
                        }}>
                          {cesEligibilityModalData.directReferralsCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Direct Referrals
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {cesEligibilityModalData.nextLevel && (
                <Card sx={{ 
                  background: `linear-gradient(135deg, #fff3e0 0%, #fff 100%)`,
                  border: `1px solid #D4AF37`,
                  borderLeft: `4px solid #D4AF37`,
                }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 600, 
                      color: '#e65100',
                      mb: 2
                    }}>
                      Next Level {cesEligibilityModalData.nextLevel.level} Requirements:
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      {(() => {
                        const ultraShortfall = Math.max(0, cesEligibilityModalData.nextLevel?.ultraShortfall || 0);
                        const directShortfall = Math.max(0, cesEligibilityModalData.nextLevel?.directReferralShortfall || 0);
                        
                        const requirements = [];
                        if (ultraShortfall > 0) {
                          requirements.push(
                            <Box key="ultra" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: '#00BFFF', 
                                mr: 1 
                              }} />
                              <Typography variant="body2">
                                Need <strong>{ultraShortfall}</strong> more Ultra Members
                              </Typography>
                            </Box>
                          );
                        }
                        if (directShortfall > 0) {
                          requirements.push(
                            <Box key="direct" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: '#1E40AF', 
                                mr: 1 
                              }} />
                              <Typography variant="body2">
                                Need <strong>{directShortfall}</strong> more Direct Referrals
                              </Typography>
                            </Box>
                          );
                        }
                        
                        return requirements.length > 0 ? requirements : (
                          <Typography variant="body2" sx={{ color: '#00BFFF', fontWeight: 500 }}>
                            ✅ Requirements completed for next level
                          </Typography>
                        );
                      })()}
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ color: '#e65100', mb: 1 }}>
                        Reward Percentage:
                      </Typography>
                      <Chip 
                        label={`${cesEligibilityModalData.nextLevel.cesPercentage}%`}
                        sx={{ 
                          background: `linear-gradient(135deg, #D4AF37, #ffb74d)`,
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          minWidth: '80px'
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setCesEligibilityModalOpen(false)}
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              fontWeight: 600,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${primaryBlue[600]} 0%, ${primaryBlue[700]} 100%)`,
              "&:hover": {
                background: `linear-gradient(135deg, ${primaryBlue[700]} 0%, ${primaryBlue[800]} 100%)`,
              },
            }}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Success Modal */}
      <OrderSuccessModal
        open={orderSuccessModal}
        onClose={handleSuccessModalClickOutside}
        onContinueShopping={handleContinueShopping}
        orderData={orderData}
        primaryBlue={primaryBlue}
      />
    </Box>
  );
};

export default CartPage;
